import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';

import Layout from '../components/lms/layout';
import VideoPlayerCard from '../components/lms/video-player-card';
import VideoListSwiper from '../components/lms/video-list-swiper';
import Button from '../atoms/button';

import { wistiaVideoContentEntryParser } from '../helpers/parser';
import SupportingDocumentCard from '../components/lms/supporting-doc-card';

const LearningCentreVideoTemplate = ({
  pageContext: { locale = 'en-AU', video, learningModule, globals },
}) => {
  const parsedVideo = wistiaVideoContentEntryParser(video);
  const learningModuleTitle = learningModule.title;
  const learningModuleSlug = learningModule.slug;

  const nextVideoLink =
    video.listPosition < learningModule.videoList.length
      ? `/academy${locale === 'en-NZ' ? '/nz' : ''}/${learningModuleSlug}/${
          learningModule.videoList[video.listPosition].slug
        }`
      : null;

  const seo = {
    pageTitle: `${parsedVideo.title} - Revolution Learning Centre`,
    pageDecription: parsedVideo.description,
  };

  const videoList = learningModule.videoList.map((videoNode) =>
    wistiaVideoContentEntryParser(videoNode)
  );

  return (
    <Layout
      bookingLink={
        locale === 'en-NZ' ? globals.nzBookingLink : globals.bookingLink
      }
      locale={locale}
    >
      <Helmet
        htmlAttributes={{
          lang: 'en',
        }}
        title={seo.pageTitle}
        link={[
          {
            rel: 'stylesheet',
            href: 'https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap',
          },
        ]}
        meta={[
          {
            name: 'description',
            content: seo.pageDecription,
          },
          {
            name: 'robots',
            content: 'noindex',
          },
        ]}
      />

      <div className="pt-header-height-mobile lg:pt-header-height-desktop xl:container mobile:px-4 tablet:px-8 lg:px-16 mb-9">
        <p className="text-body-xs text-white mobile+tablet:whitespace-nowrap">
          <a
            className="underline hover:no-underline"
            href={`/academy${locale === 'en-NZ' ? '/nz' : ''}`}
          >
            <span>Learning Centre</span>
          </a>{' '}
          /{' '}
          <a
            className="underline hover:no-underline"
            href={`/academy${
              locale === 'en-NZ' ? '/nz' : ''
            }/${learningModuleSlug}`}
          >
            <span>{learningModuleTitle}</span>
          </a>{' '}
          / {parsedVideo.title}
        </p>
      </div>

      <div className="xl:container mobile:px-4 tablet:px-8 lg:px-16 mb-4">
        <h3 className="u-h2 md:u-h3 text-white">{learningModuleTitle}</h3>
      </div>

      <div className="xl:container mobile:px-4 tablet:px-8 lg:px-16 mb-15">
        <VideoPlayerCard
          wistiaId={parsedVideo.wistiaVideo.hashed_id}
          title={parsedVideo.title}
          description={parsedVideo.description}
          position={video.listPosition}
          videoListLength={learningModule.videoList.length}
          nextVideoLink={nextVideoLink}
        />
      </div>

      {video.supportingDocumentations &&
        video.supportingDocumentations.length > 0 && (
          <div className="xl:container mobile:px-4 tablet:px-8 lg:px-16 mb-16">
            <p className="text-body-lg font-medium text-white mb-4">
              Supporting topic documentation
            </p>
            <div className="space-y-6">
              {video.supportingDocumentations.map((doc, index) => (
                <SupportingDocumentCard
                  key={`doc-${index}`}
                  title={doc.title}
                  description={doc.description.description}
                  steps={doc.steps}
                />
              ))}
            </div>
          </div>
        )}

      <div className="xl:container mobile:px-4 tablet:px-8 lg:px-16 mb-16">
        <VideoListSwiper
          parentSlug={learningModule.slug}
          videoList={videoList}
          heading="Other topics in this module"
          mode="dark"
          activeIndex={video.listPosition - 1}
        />
      </div>

      <div className="xl:container mobile:px-4 tablet:px-8 lg:px-16 mb-12 md:mb-32 flex justify-center">
        <Button
          className="text-white"
          variant="quaternary"
          as="a"
          href={`/academy${locale === 'en-NZ' ? '/nz' : ''}`}
          label="Back to learning centre"
        />
      </div>
    </Layout>
  );
};

LearningCentreVideoTemplate.propTypes = {
  pageContext: PropTypes.object.isRequired,
};

export default LearningCentreVideoTemplate;
